import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowRight,
  Bookmark,
  Check,
  Copy,
  Download,
  FileText,
  KeyRound,
  MailCheck,
  PackageCheck,
  Play,
  PlayCircle,
  RefreshCw,
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import type { FulfillmentType, OrderStatusData } from '../lib/api';
import { copyText } from '../lib/clipboard';
import { formatClock, formatPrice } from '../lib/format';
import { useOrder } from '../lib/queries';

function formatTime(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
}

/* Stepper */

type Step = { label: string; done: boolean; active: boolean; time: string | null };

function Stepper({ order }: { order: OrderStatusData }) {
  const paid = order.status === 'PAID' || order.status === 'DELIVERED';
  const delivered = order.status === 'DELIVERED' || Boolean(order.delivery);

  const steps: Step[] = [
    { label: 'Objednáno', done: true, active: false, time: null },
    { label: 'Zaplaceno', done: paid, active: !paid, time: formatTime(order.paid_at) },
    {
      label: 'Doručeno',
      done: delivered,
      active: paid && !delivered,
      time: formatTime(order.delivered_at),
    },
  ];

  return (
    <section className="rounded-card border border-line bg-card p-5 shadow-card">
      <ol className="flex items-start">
        {steps.map((step, i) => (
          <li key={step.label} className="flex flex-1 items-start">
            <div className="flex flex-1 flex-col items-center gap-1.5">
              <span
                className={`grid h-9 w-9 place-items-center rounded-full ${
                  step.done
                    ? 'bg-success text-white'
                    : step.active
                      ? 'board text-chalk'
                      : 'bg-paper2 text-ink-soft'
                }`}
              >
                {step.label === 'Doručeno' && step.done ? (
                  <PackageCheck className="h-4 w-4" strokeWidth={2.2} />
                ) : step.done ? (
                  <Check className="h-4 w-4" strokeWidth={3} />
                ) : (
                  <span
                    className={`h-2.5 w-2.5 rounded-full bg-current ${step.active ? 'motion-safe:animate-pulse' : ''}`}
                  />
                )}
              </span>
              <span
                className={`text-xs font-semibold ${step.done ? 'text-success' : step.active ? 'text-board' : 'text-ink-soft'}`}
              >
                {step.label}
              </span>
              {step.time && (
                <span className="text-[11px] tabular-nums text-ink-soft">{step.time}</span>
              )}
            </div>
            {i < steps.length - 1 && (
              <span
                className={`mt-4 h-1 flex-1 rounded ${step.done ? 'bg-success' : 'bg-line'}`}
                aria-hidden
              />
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

/* Doručený obsah */

function DeliveryReloadNote({ onReload, pending }: { onReload: () => void; pending: boolean }) {
  return (
    <p className="mt-2.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-ink-soft">
      <RefreshCw className="h-3.5 w-3.5 flex-none" strokeWidth={2} />
      Odkaz vypršel?
      <button
        type="button"
        onClick={onReload}
        disabled={pending}
        className="font-semibold text-board underline underline-offset-2 disabled:opacity-60"
      >
        {pending ? 'Načítám…' : 'Vytvořit nový odkaz'}
      </button>
    </p>
  );
}

function DeliverySection({
  order,
  onReload,
  reloading,
}: {
  order: OrderStatusData;
  onReload: () => void;
  reloading: boolean;
}) {
  const delivery = order.delivery ?? null;
  const type: FulfillmentType = delivery?.fulfillment_type ?? order.product.fulfillment_type;

  // Obsah ještě nedorazil (WP6 dodá `delivery`), vlídný mezistav.
  if (!delivery) {
    return (
      <div className="mt-3 rounded-card border border-line bg-card p-5 text-center shadow-card">
        <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-paper2 text-ink-soft">
          <RefreshCw className="h-6 w-6 motion-safe:animate-spin" strokeWidth={2} />
        </span>
        <p className="mt-3 font-semibold text-ink">Připravujeme váš obsah…</p>
        <p className="mt-1 text-sm text-ink-soft">
          Platba je spárovaná. Doručení se obvykle zpřístupní během chvíle.
        </p>
        <DeliveryReloadNote onReload={onReload} pending={reloading} />
      </div>
    );
  }

  if (type === 'EMAIL') {
    return (
      <div className="mt-3 flex items-start gap-3 rounded-card border border-info/30 bg-info/10 p-4">
        <MailCheck className="mt-0.5 h-5 w-5 flex-none text-info" strokeWidth={2} />
        <div className="text-sm leading-relaxed">
          <p className="font-semibold text-info">Obsah dorazí e-mailem</p>
          <p className="text-ink-soft">
            {delivery.email_sent_to
              ? `Poslali jsme ho na ${delivery.email_sent_to}.`
              : 'Poslali jsme ho na váš e-mail.'}{' '}
            Nepřišel do 5&nbsp;minut? Mrkněte do spamu.
          </p>
        </div>
      </div>
    );
  }

  if (type === 'LICENSE_KEY') {
    const key = delivery.license_key ?? '';
    return (
      <div className="mt-3 rounded-card border border-line bg-card p-4 shadow-card">
        <div className="flex items-center gap-3">
          <span className="board grid h-12 w-12 flex-none place-items-center rounded-xl text-chalk">
            <KeyRound className="h-6 w-6" strokeWidth={2} />
          </span>
          <div className="min-w-0 grow">
            <p className="font-semibold leading-snug">Licenční klíč</p>
            <p className="text-sm text-ink-soft">Aktivujete v aplikaci v menu „Zadat licenci".</p>
          </div>
        </div>
        <div className="board mt-3.5 flex items-center justify-between gap-3 rounded-xl px-4 py-3.5">
          <code className="truncate font-mono text-sm tracking-widest text-accent sm:text-base">
            {key || '…'}
          </code>
          {key && (
            <button
              type="button"
              onClick={() => copyText(key, 'Licenční klíč')}
              className="inline-flex h-10 flex-none items-center gap-1.5 rounded-lg border border-chalk/25 px-3 text-sm font-semibold text-chalk transition hover:bg-white/10"
            >
              <Copy className="h-4 w-4" strokeWidth={2} />
              Kopírovat
            </button>
          )}
        </div>
      </div>
    );
  }

  if (type === 'STREAM') {
    const lessons = delivery.lessons ?? [];
    return (
      <div className="mt-3 space-y-3">
        {lessons.length === 0 && (
          <p className="rounded-card border border-line bg-card p-4 text-sm text-ink-soft shadow-card">
            Lekce se právě připravují.
          </p>
        )}
        {lessons.map((lesson) => (
          <div key={lesson.id} className="rounded-card border border-line bg-card p-4 shadow-card">
            <div className="flex items-center gap-3">
              <span className="board grid h-12 w-12 flex-none place-items-center rounded-xl text-chalk">
                <PlayCircle className="h-6 w-6" strokeWidth={2} />
              </span>
              <div className="min-w-0 grow">
                <p className="font-semibold leading-snug">{lesson.title}</p>
                <p className="text-sm text-ink-soft">Stream · {formatClock(lesson.duration_s)}</p>
              </div>
            </div>
            <a
              href={lesson.url}
              target="_blank"
              rel="noreferrer"
              className="mt-3.5 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-board font-semibold text-chalk transition hover:bg-board-deep active:scale-[.98]"
            >
              <Play className="h-5 w-5" strokeWidth={2} />
              Přehrát
            </a>
          </div>
        ))}
        <DeliveryReloadNote onReload={onReload} pending={reloading} />
      </div>
    );
  }

  // DOWNLOAD
  const downloads = delivery.downloads ?? [];
  return (
    <div className="mt-3 space-y-3">
      {downloads.length === 0 && (
        <p className="rounded-card border border-line bg-card p-4 text-sm text-ink-soft shadow-card">
          Soubory se právě připravují.
        </p>
      )}
      {downloads.map((file) => (
        <div
          key={file.asset_id}
          className="rounded-card border border-line bg-card p-4 shadow-card"
        >
          <div className="flex items-center gap-3">
            <span className="board grid h-12 w-12 flex-none place-items-center rounded-xl text-chalk">
              <FileText className="h-6 w-6" strokeWidth={2} />
            </span>
            <div className="min-w-0 grow">
              <p className="truncate font-semibold leading-snug">{file.filename}</p>
              <p className="text-sm text-ink-soft">Soubor ke stažení</p>
            </div>
          </div>
          <a
            href={file.url}
            className="mt-3.5 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-success font-semibold text-white transition hover:brightness-95 active:scale-[.98]"
          >
            <Download className="h-5 w-5" strokeWidth={2} />
            Stáhnout
          </a>
        </div>
      ))}
      <DeliveryReloadNote onReload={onReload} pending={reloading} />
    </div>
  );
}

/* Stránka */

function ConfirmationSkeleton() {
  return (
    <div className="mx-auto max-w-2xl space-y-5 px-4 py-8">
      <div className="mx-auto h-20 w-20 rounded-full bg-paper2 motion-safe:animate-pulse" />
      <div className="h-24 rounded-card bg-paper2 motion-safe:animate-pulse" />
      <div className="h-40 rounded-card bg-paper2 motion-safe:animate-pulse" />
    </div>
  );
}

export default function OrderConfirmation() {
  const { token } = useParams<{ token: string }>();
  const reduce = useReducedMotion();

  const query = useOrder(token);
  const order = query.data;
  const { isLoading, isError, refetch, isFetching } = query;

  // Dopolluj, dokud po zaplacení nedorazí doručení (jinak stránka je statická).
  const needsDelivery = order?.status === 'PAID' && !order.delivery;
  const polling = useOrder(token, { poll: needsDelivery });

  if (isLoading) {
    return (
      <div className="grid-paper min-h-[70vh]">
        <ConfirmationSkeleton />
      </div>
    );
  }

  if (isError || !order) {
    return (
      <div className="grid-paper min-h-[70vh]">
        <div className="mx-auto max-w-2xl px-4 py-20 text-center">
          <h1 className="text-3xl">Objednávka nenalezena</h1>
          <p className="mt-3 text-ink-soft">Tento odkaz je neplatný nebo vypršel.</p>
          <Link
            to="/"
            className="mt-6 inline-flex h-12 items-center rounded-xl bg-board px-6 font-semibold text-chalk transition hover:bg-board-deep"
          >
            Zpět do katalogu
          </Link>
        </div>
      </div>
    );
  }

  const paid = order.status === 'PAID' || order.status === 'DELIVERED';
  const delivered = order.status === 'DELIVERED' || Boolean(order.delivery);
  const reload = () => {
    refetch();
    polling.refetch();
  };

  const entrance = reduce
    ? {}
    : {
        initial: { opacity: 0, y: 8 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.25 },
      };

  return (
    <div className="grid-paper min-h-[70vh] pb-16">
      <motion.main className="mx-auto max-w-2xl px-4 py-6" {...entrance}>
        {/* Úspěch */}
        <section className="text-center">
          <span
            className={`mx-auto grid h-20 w-20 place-items-center rounded-full ${
              paid ? 'bg-success/10 text-success' : 'bg-paper2 text-ink-soft'
            }`}
          >
            <Check className="h-10 w-10" strokeWidth={3} />
          </span>
          <h1 className="mt-4 text-2xl md:text-3xl">
            {delivered
              ? 'Materiál je připravený'
              : paid
                ? 'Platbu jsme přijali.'
                : 'Objednávka čeká na platbu'}
          </h1>
          <p className="mt-2 leading-relaxed text-ink-soft">
            {paid ? (
              <>
                Platba {formatPrice(order.amount_czk)} se spárovala.
                {delivered
                  ? ' Materiál máte připravený níže.'
                  : ' Materiál připravujeme, obvykle to trvá jen chvíli.'}
                {order.delivery?.email_sent_to ? (
                  <>
                    {' '}
                    Vše jsme poslali i na{' '}
                    <span className="font-semibold text-ink">{order.delivery.email_sent_to}</span>.
                  </>
                ) : null}
              </>
            ) : (
              'Jakmile dorazí platba, materiál se zpřístupní tady i v e-mailu.'
            )}
          </p>
        </section>

        <div className="mt-7">
          <Stepper order={order} />
        </div>

        {/* Doručený obsah */}
        <section className="mt-6">
          <h2 className="text-xl">Váš obsah</h2>
          <p className="mt-1 text-sm text-ink-soft">{order.product.title}</p>
          <DeliverySection order={order} onReload={reload} reloading={isFetching} />
        </section>

        {/* E-mail info */}
        <section className="mt-6 flex items-start gap-3 rounded-card border border-info/30 bg-info/10 p-4">
          <MailCheck className="mt-0.5 h-5 w-5 flex-none text-info" strokeWidth={2} />
          <div className="text-sm leading-relaxed">
            <p className="font-semibold text-info">Vše máte i v e-mailu</p>
            <p className="text-ink-soft">
              Poslali jsme potvrzení s odkazy a zjednodušený daňový doklad. Nepřišel do
              5&nbsp;minut? Mrkněte do spamu, nebo napište na{' '}
              <a
                href="mailto:pf.kabinet.napadu@gmail.com"
                className="font-semibold underline underline-offset-2"
              >
                pf.kabinet.napadu@gmail.com
              </a>
              .
            </p>
          </div>
        </section>

        {/* Trvalý odkaz */}
        <section className="mt-3 flex items-start gap-3 rounded-card border border-line bg-card p-4">
          <Bookmark className="mt-0.5 h-5 w-5 flex-none text-board" strokeWidth={2} />
          <p className="text-sm leading-relaxed text-ink-soft">
            Tuto stránku si můžete uložit jako záložku. Budete tak mít trvalý přístup ke svému
            nákupu a odkazy si tu kdykoliv obnovíte.
          </p>
        </section>

        <Link
          to="/"
          className="mt-8 flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-line bg-card font-semibold text-ink transition hover:border-board active:scale-[.98]"
        >
          Zpět do katalogu
          <ArrowRight className="h-4 w-4" strokeWidth={2} />
        </Link>
      </motion.main>
    </div>
  );
}
